import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { getToken, getCachedUser, setCachedUser, setToken, api } from './api';
import { loadAllData, refreshCache } from './store';
import Auth from './components/Auth';
import Header from './components/Header';
import Navigation from './components/Navigation';
import Dashboard from './components/Dashboard';
import Transactions from './components/Transactions';
import Templates from './components/Templates';
import Categories from './components/Categories';
import Accounts from './components/Accounts';
import Periods from './components/Periods';
import Statistics from './components/Statistics';
import HelpModal from './components/HelpModal';
import Onboarding from './components/Onboarding';
import NotFound from './components/NotFound';
import Modal from './components/Modal';
import TransactionForm from './components/TransactionForm';
import TypePicker from './components/TypePicker';
import Profile from './components/Profile';
import Spinner from './components/Spinner';

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [helpOpen, setHelpOpen] = useState(false);
  const [showTxForm, setShowTxForm] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [showOnboard, setShowOnboard] = useState(false);

  const maybeShowHelp = (userId) => {
    if (!localStorage.getItem(`kopilka_helpSeen_${userId}`)) {
      setHelpOpen(true);
    }
  };

  const closeHelp = () => {
    setHelpOpen(false);
    if (user) localStorage.setItem(`kopilka_helpSeen_${user.id}`, '1');
  };

  const handleAuth = async (result) => {
    setToken(result.token);
    setCachedUser(result.user);
    setUser(result.user);
    // Сразу подтягиваем всю историю пользователя из JSON и кладём в браузер
    try {
      await loadAllData(result.user.id);
    } catch (err) {
      // ignore
    }
    // Справка при первом входе
    maybeShowHelp(result.user.id);
    // Показываем онбординг при первом входе
    const seen = localStorage.getItem(`kopilka_onboard_${result.user.id}`);
    if (!seen) setShowOnboard(true);
  };

  const handleLogout = async () => {
    try {
      await api.logout();
    } catch (err) {
      // ignore
    }
    localStorage.removeItem('kopilka_token');
    localStorage.removeItem('kopilka_user');
    setUser(null);
  };

  useEffect(() => {
    const init = async () => {
      const cachedUser = getCachedUser();
      if (!getToken() || !cachedUser) {
        setLoading(false);
        return;
      }
      // Мгновенно показываем пользователя из памяти браузера
      setUser(cachedUser);
      try {
        // Проверяем сессию на сервере и обновляем профиль
        const me = await api.me();
        setCachedUser(me);
        setUser(me);
        // Подтягиваем всю историю из JSON-файла
        refreshCache();
        maybeShowHelp(me.id);
      } catch (err) {
        localStorage.removeItem('kopilka_token');
        localStorage.removeItem('kopilka_user');
        setUser(null);
      } finally {
        setLoading(false);
      }
    };
    init();
  }, []);

  useEffect(() => {
    const onLogout = () => {
      localStorage.removeItem('kopilka_token');
      localStorage.removeItem('kopilka_user');
      setUser(null);
    };
    const onOpenProfile = () => setShowProfile(true);
    window.addEventListener('auth:logout', onLogout);
    window.addEventListener('open:profile', onOpenProfile);
    return () => {
      window.removeEventListener('auth:logout', onLogout);
      window.removeEventListener('open:profile', onOpenProfile);
    };
  }, []);

  if (loading) {
    return (
      <div className="loading-screen">
        <Spinner label="Загрузка..." />
      </div>
    );
  }

  if (!user) {
    return <Auth onAuth={handleAuth} />;
  }

  return (
    <Router>
      <MainApp
        user={user}
        handleLogout={handleLogout}
        setHelpOpen={setHelpOpen}
        showOnboard={showOnboard}
        setShowOnboard={setShowOnboard}
        showProfile={showProfile}
        setShowProfile={setShowProfile}
        helpOpen={helpOpen}
        closeHelp={closeHelp}
      />
    </Router>
  );
}

// Отдельный компонент внутри Router, чтобы использовать useLocation
function MainApp({ user, handleLogout, setHelpOpen, showOnboard, setShowOnboard, showProfile, setShowProfile, helpOpen, closeHelp }) {
  const location = useLocation();
  const isHome = location.pathname === '/';
  const [showPicker, setShowPicker] = useState(false);
  const [txType, setTxType] = useState(null);
  const [showTxForm, setShowTxForm] = useState(false);

  const openPicker = () => setShowPicker(true);

  const handleTypePick = (type) => {
    setShowPicker(false);
    setTxType(type);
    setShowTxForm(true);
  };

  const closeTxForm = () => {
    setShowTxForm(false);
    setTxType(null);
  };

  const handleTxSave = async (data) => {
    try {
      await api.createTransaction(data);
      await loadAllData(user.id);
      // Сообщаем главной, что данные изменились — суточный остаток пересчитается сразу
      window.dispatchEvent(new Event('data:changed'));
    } catch (err) {
      console.error(err);
    }
    closeTxForm();
  };

  return (
      <div className="app-container">
        <Header user={user} onLogout={handleLogout} onOpenHelp={() => setHelpOpen(true)} />
        {showPicker && (
          <Modal title="Новая транзакция" onClose={() => setShowPicker(false)}>
            <TypePicker
              onPick={handleTypePick}
              onCancel={() => setShowPicker(false)}
            />
          </Modal>
        )}
        {showTxForm && (
          <Modal title="Новая транзакция" onClose={closeTxForm}>
            <TransactionForm
              presetType={txType}
              onSave={handleTxSave}
              onCancel={closeTxForm}
            />
          </Modal>
        )}
        <Routes>
          <Route path="/" element={<Dashboard user={user} />} />
          <Route path="/transactions" element={<Transactions />} />
          <Route path="/templates" element={<Templates />} />
          <Route path="/categories" element={<Categories />} />
          <Route path="/accounts" element={<Accounts />} />
          <Route path="/periods" element={<Periods />} />
          <Route path="/statistics" element={<Statistics />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
        <Navigation />
        {isHome && <button className="fab" onClick={openPicker}>+</button>}
        {showOnboard && (
          <Onboarding user={user} onFinish={() => setShowOnboard(false)} />
        )}
        {showProfile && <Profile onClose={() => setShowProfile(false)} />}
        {helpOpen && <HelpModal onClose={closeHelp} />}
      </div>
  );
}

export default App;
